import { useState } from 'react';
import { useStore } from 'zustand';

import { variationTableFor } from '@/domain/construction';
import { parseExpression, VariationProblem } from '@/domain/expression';
import { type Entity } from '@/domain/model';
import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';
import { MathField } from './MathField';
import { MathText } from './MathText';
import { colour } from './theme';

/**
 * Drawing `y = f(x)`.
 *
 * ## The formula editor
 *
 * `MathField` wraps MathLive, so a fraction is built and read as a fraction rather than as
 * `(x+1)/(x-1)`. It carries LaTeX in and out, and the parser reads LaTeX directly — there is
 * no conversion step between the editor and the document, which is what stops a formula being
 * degraded by the round trip of editing, saving and reopening.
 *
 * Two things a WYSIWYG field does not give you, kept from the plain version: **the error says
 * where it is**, and there are examples to start from.
 *
 * ## Errors as you type, not on submit
 *
 * The formula is parsed on every keystroke and the message appears under the field. Waiting
 * for a button press means the user finishes a long expression before learning that the third
 * character was wrong.
 *
 * Nothing is *drawn* until the field is committed, though — a half-typed `sin(` would flash a
 * broken curve on every keystroke, and a canvas that flickers while you type is worse than one
 * that waits.
 */

type GraphEntity = Entity & {
  readonly source: Extract<Entity['source'], { op: 'function-graph' }>;
};

/**
 * Starting points, not a template gallery.
 *
 * Each is a *shape* a teacher reaches for — a parabola, a wave, a hyperbola, a root — chosen so
 * that between them they cover what the sampler has to get right. Clicking one fills the field
 * rather than drawing immediately, so it reads as a starting point to edit.
 *
 * Written in LaTeX and rendered as mathematics. They were plain monospace text until a user
 * pointed out the obvious: a panel whose whole point is that formulas *look like formulas*
 * should not offer its examples as source code. `\frac{1}{x}` and `1/x` are the same
 * expression to the parser, so the choice costs nothing but the rendering.
 */
const EXAMPLES: readonly string[] = [
  'x^2',
  'x^3-3x',
  String.raw`\sin(x)`,
  String.raw`\frac{1}{x}`,
  String.raw`\sqrt{x}`,
  '2^x',
];

export interface GraphPanelProps {
  readonly store: EditorStore;
}

export function GraphPanel({ store }: GraphPanelProps) {
  const document = useStore(store, (state) => state.document);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  const graphs = [...selection]
    .map((id) => document.entities.get(id))
    .filter((entity): entity is GraphEntity => entity?.source.op === 'function-graph');

  const editing = graphs.length === 1 ? graphs[0] : undefined;

  const [draft, setDraft] = useState('x^2');
  const [touched, setTouched] = useState(false);

  // The field follows the selection until the user types, at which point it is theirs.
  const value = touched ? draft : (editing?.source.expression ?? draft);
  const problem = errorIn(value);

  const commit = (): void => {
    if (problem !== null) return;

    const accepted =
      editing === undefined
        ? commands.addFunctionGraph(value)
        : commands.setFunctionGraph([editing.id], { expression: value });

    if (accepted) setTouched(false);
  };

  return (
    <PanelSection
      title="Đồ thị hàm số"
      {...(editing === undefined ? {} : { badge: 'đang sửa' })}
      initiallyCollapsed={editing === undefined}
      // Folded when nothing is selected, and unfolded the moment a curve is — otherwise a
      // section that folded itself at mount stays folded all session, and the controls for the
      // selected graph are behind a header nothing invites you to click.
      openWhen={editing !== undefined}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: colour.muted, fontSize: 11 }}>y =</span>
        <MathField
          value={value}
          invalid={problem !== null}
          onChange={(latex) => {
            setTouched(true);
            setDraft(latex);
          }}
          onSubmit={commit}
        />
      </div>

      {problem !== null && (
        <p style={{ margin: 0, color: colour.danger, fontSize: 11, lineHeight: 1.4 }}>
          {problem.message}
          {/*
            The offending part of the source, quoted.
            A caret under the raw text was right for a plain input and is meaningless beside a
            typeset formula — the character the parser stopped at is not where it appears on
            screen. Quoting the fragment points at the problem in terms the user can match.
          */}
          <br />
          <code style={{ color: colour.faint }}>{fragmentAt(value, problem.at)}</code>
        </p>
      )}

      <button
        type="button"
        onClick={commit}
        disabled={problem !== null}
        style={{
          padding: '6px 0',
          borderRadius: 4,
          border: 'none',
          background: problem === null ? colour.accent : colour.wash,
          color: problem === null ? colour.solid : colour.faint,
          font: '600 12px var(--es-font)',
          cursor: problem === null ? 'pointer' : 'not-allowed',
        }}
      >
        {editing === undefined ? 'Vẽ đồ thị' : 'Cập nhật'}
      </button>

      {editing !== undefined && <Range graph={editing} store={store} />}
      {editing !== undefined && <Marks graph={editing} store={store} />}
      {editing !== undefined && <VariationTable graph={editing} store={store} />}

      <Axes store={store} {...(editing === undefined ? {} : { graph: editing })} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {EXAMPLES.map((example) => (
          <button
            key={example}
            type="button"
            onClick={() => {
              setTouched(true);
              setDraft(example);
            }}
            className="es-btn"
            style={{ minHeight: 27, padding: '2px 9px', fontSize: 13 }}
          >
            <MathText latex={example} />
          </button>
        ))}
      </div>

      <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
        Gõ thẳng trong ô công thức — phân số, căn, luỹ thừa hiện đúng dạng toán. Khung hình tự khớp
        theo đường cong, nên cứ viết đúng hàm số đề cho; phần đuôi vượt ra ngoài thì bị cắt chứ
        không bị bóp lại.
      </p>
    </PanelSection>
  );
}

/**
 * The coordinate axes.
 *
 * Offered here rather than in a section of its own because this is where a user is when they
 * discover they need them — and because the first graph brings a pair along anyway, so the
 * control is mostly for the case of *no* graph at all: plotting points, reading coordinates,
 * showing a translation.
 */
function Axes({
  store,
  graph,
}: {
  readonly store: EditorStore;
  /** The graph being edited, when there is one. Without it there is nothing to fit to. */
  readonly graph?: GraphEntity;
}) {
  const document = useStore(store, (state) => state.document);
  const commands = useStore(store, (state) => state.commands);

  const axes = [...document.entities.values()].find((entity) => entity.source.op === 'axes');

  if (axes === undefined) {
    return (
      <button
        type="button"
        onClick={() => {
          commands.addAxes();
        }}
        className="es-btn"
        style={{ padding: '5px 0', fontSize: 11 }}
      >
        + Hệ trục toạ độ
      </button>
    );
  }

  const source = axes.source;
  if (source.op !== 'axes') return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: colour.muted }}>
        <input
          type="checkbox"
          checked={source.numbered}
          onChange={(event) => {
            commands.setAxes([axes.id], { numbered: event.target.checked });
          }}
        />
        <span style={{ fontSize: 11 }}>Ghi số trên trục</span>
      </label>

      {/*
        The tick spacing, which the fitted frame chooses and a teacher sometimes has to overrule.
        `graphWindow` aims at about five marks across, which is right for reading a shape and
        wrong for a figure whose whole point is a value at x = 0.5.

        One per axis, because the two axes rarely reach the same distance. A step that suits an
        axis reaching 37 units puts nothing at all on one reaching 1.
      */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
        <NumberField
          label="Bước chia Ox"
          value={source.step}
          step={0.5}
          onCommit={(value) => {
            if (value > 0) commands.setAxes([axes.id], { step: value });
          }}
        />
        <NumberField
          label="Bước chia Oy"
          value={source.stepY ?? source.step}
          step={0.5}
          onCommit={(value) => {
            if (value > 0) commands.setAxes([axes.id], { stepY: value });
          }}
        />
      </div>

      {/*
        The y unit, as a multiple of the x unit.

        Shown as a ratio rather than as world units per mathematical unit, because the ratio is
        the thing with meaning: 1 is square — a circle stays a circle — and anything below it says
        how much the y axis has been compressed to keep a tall function on the page. The number
        underneath is not something a teacher should have to hold in their head.
      */}
      <NumberField
        label="Đơn vị Oy so với Ox"
        value={Number(((source.scaleY ?? source.scale) / source.scale).toFixed(3))}
        step={0.1}
        onCommit={(value) => {
          if (value > 0) commands.setAxes([axes.id], { scaleY: source.scale * value });
        }}
      />

      {graph !== undefined && (
        <button
          type="button"
          onClick={() => {
            commands.fitAxesTo(graph.id);
          }}
          title="Đặt lại khung, bước chia và đơn vị hai trục cho vừa đường cong"
          className="es-btn"
          style={{ padding: '7px 0', fontSize: 11 }}
        >
          Vừa khung
        </button>
      )}
    </div>
  );
}

/**
 * Which notable points are marked, and how.
 *
 * ## Why the boxes edit one object rather than adding dots
 *
 * A mark placed as a plain point is correct when it is made and silently wrong from the next edit
 * on: retype the formula and the dot stays at the old curve's turning point, still carrying the
 * old coordinates. So the marks are one derived object that names the graph, and these boxes
 * change what it shows. The cost is that a single dot cannot be deleted — and a dot stranded on
 * the wrong curve could not be fixed at all.
 *
 * ## Why guides and coordinates are one choice and not two
 *
 * They are two ways of saying the same thing and a textbook does one or the other; drawing both
 * doubles the ink to repeat itself. Which is right depends on the question — "tìm f(2) dựa vào
 * đồ thị" wants the reader's eye to travel to the axis, and a coordinate printed beside the dot
 * answers the question the figure was supposed to pose.
 */
function Marks({ graph, store }: { readonly graph: GraphEntity; readonly store: EditorStore }) {
  const document = useStore(store, (state) => state.document);
  const commands = useStore(store, (state) => state.commands);

  const existing = [...document.entities.values()].find(
    (entity) => entity.source.op === 'graph-marks' && entity.source.graph === graph.id
  );
  const source = existing?.source.op === 'graph-marks' ? existing.source : null;
  const shown = new Set(source?.kinds ?? []);

  const toggle = (kinds: readonly string[], on: boolean): void => {
    const next = new Set(shown);
    for (const kind of kinds) {
      if (on) next.add(kind);
      else next.delete(kind);
    }
    commands.setGraphMarks(graph.id, { kinds: [...next] });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="es-eyebrow">Đánh dấu điểm</span>
      {MARK_BOXES.map((box) => (
        <label
          key={box.label}
          style={{ display: 'flex', alignItems: 'center', gap: 6, color: colour.body }}
        >
          <input
            type="checkbox"
            checked={box.kinds.every((kind) => shown.has(kind))}
            onChange={(event) => {
              toggle(box.kinds, event.target.checked);
            }}
          />
          <span style={{ fontSize: 11 }}>{box.label}</span>
        </label>
      ))}

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: colour.body }}>
        <input
          type="checkbox"
          checked={source?.guides ?? false}
          disabled={shown.size === 0}
          onChange={(event) => {
            commands.setGraphMarks(graph.id, { guides: event.target.checked });
          }}
        />
        <span style={{ fontSize: 11, color: shown.size === 0 ? colour.faint : colour.body }}>
          Kẻ đường gióng tới hai trục
        </span>
      </label>

      <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
        {source?.guides === true
          ? 'Số được ghi trên trục, không ghi toạ độ cạnh chấm — hợp với đề bắt đọc số từ đồ thị.'
          : 'Toạ độ ghi ngay cạnh chấm. Sửa công thức thì các điểm tự tính lại.'}
      </p>
    </div>
  );
}

/**
 * The boxes, in the order a lesson asks for them.
 *
 * `cực trị` is one box over two kinds because that is one idea to the person ticking it, even
 * though the domain has to tell a maximum from a minimum.
 */
const MARK_BOXES: readonly { readonly label: string; readonly kinds: readonly string[] }[] = [
  { label: 'Cực trị', kinds: ['maximum', 'minimum'] },
  { label: 'Giao với trục hoành', kinds: ['root'] },
  { label: 'Giao với trục tung', kinds: ['intercept'] },
];

/**
 * The bảng biến thiên of the graph being edited.
 *
 * ## Why it says no *before* the click
 *
 * Not every function has one. `floor(x)` has no derivative; a constant has nothing to
 * tabulate. The button could be offered anyway and simply do nothing, which is what a
 * disabled-looking control that still accepts clicks amounts to — the user presses it,
 * nothing appears on the canvas, and there is no way to find out why.
 *
 * So the table is built here, from the same function the evaluator will use, and the panel
 * either offers the button or says what is in the way. It is the same rule the construction
 * tools follow with their previews: what you can see is what you will get.
 */
function VariationTable({
  graph,
  store,
}: {
  readonly graph: GraphEntity;
  readonly store: EditorStore;
}) {
  const document = useStore(store, (state) => state.document);
  const commands = useStore(store, (state) => state.commands);

  const existing = [...document.entities.values()].find(
    (entity) => entity.source.op === 'variation-table' && entity.source.graph === graph.id
  );

  const result = variationTableFor(graph.source.expression, {
    from: graph.source.from,
    to: graph.source.to,
  });

  // An unreadable formula is already reported above, in its own words; saying it twice would
  // put two red messages under one mistake.
  if (result === null) return null;

  if (!result.ok) {
    return (
      <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
        {WHY_NOT[result.problem]}
      </p>
    );
  }

  /*
   * A way back to the table, rather than a sentence about it.
   *
   * This branch used to be prose and nothing else, which made the section a dead end: the only
   * route from here to the table was to know it was on the canvas and click it. Delete it and
   * there was no route at all — the button does not return until the curve is selected again,
   * and nothing said so.
   *
   * One control in this slot at all times, doing whichever of the two things is possible: make
   * the table, or go to it. Selecting it is what the rest of the editor is for — dragging it,
   * restyling it, deleting it — so the loop closes without the panel having to grow a delete of
   * its own.
   */
  if (existing !== undefined) {
    const table = existing;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button
          type="button"
          onClick={() => {
            commands.select([table.id]);
          }}
          title="Chọn bảng biến thiên trên bản vẽ — rồi kéo, đổi kiểu hoặc xoá"
          className="es-btn"
          style={{ padding: '5px 0', fontSize: 11 }}
        >
          Chọn bảng biến thiên
        </button>
        <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
          Sửa công thức thì bảng tự cập nhật theo. Xoá bảng rồi chọn lại đường cong thì nút tạo hiện
          lại.
        </p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        commands.addVariationTable(graph.id);
      }}
      title="Vẽ bảng biến thiên của hàm này — dùng được cả khi in ra PDF"
      className="es-btn"
      style={{ padding: '5px 0', fontSize: 11 }}
    >
      + Bảng biến thiên
    </button>
  );
}

/** Why a function has no table, in the words a teacher would use. */
const WHY_NOT: Readonly<Record<VariationProblem, string>> = {
  [VariationProblem.NoDerivative]:
    'Hàm này không có đạo hàm (floor, ceil, round, sign, min, max) nên không lập được bảng biến thiên.',
  [VariationProblem.Constant]: 'Hàm hằng — đồ thị nằm ngang, không có chiều biến thiên để ghi.',
  [VariationProblem.TooManyPoints]:
    'Hàm có quá nhiều điểm cực trị trong khoảng đang xét. Thu hẹp khoảng x lại rồi thử lại.',
  [VariationProblem.Empty]: 'Hàm không xác định ở đâu trong khoảng đang xét.',
};

/** The x-range of the graph being edited. Shown only while editing, because it needs a subject. */
function Range({ graph, store }: { readonly graph: GraphEntity; readonly store: EditorStore }) {
  const commands = useStore(store, (state) => state.commands);
  const { from, to } = graph.source;

  /*
   * Two ends, because most problems have two.
   *
   * This was one slider held symmetric about zero, with a comment saying that an asymmetric
   * window "puts the y axis off-centre and the picture stops looking like the textbook". That
   * was true while the frame was symmetric and it is the opposite of true now: the frame is
   * fitted to the curve, a textbook draws the part of the plane the problem is about, and
   * `[-1; 5]` is the interval half the exercises state. The script could express it and the
   * panel could not, which is the sort of gap that makes one door the real one.
   *
   * The frame follows the range. A window that no longer contains its own curve is the defect
   * the fitted frame exists to prevent, and leaving it to the user to notice and press a button
   * would reintroduce it every time an interval is nudged.
   */
  const setEnd = (end: 'from' | 'to', value: number): void => {
    const next = end === 'from' ? { from: value, to } : { from, to: value };
    if (!(next.to > next.from)) return;
    if (commands.setFunctionGraph([graph.id], next)) commands.fitAxesTo(graph.id);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
      <NumberField
        label="x từ"
        value={from}
        onCommit={(value) => {
          setEnd('from', value);
        }}
      />
      <NumberField
        label="đến"
        value={to}
        onCommit={(value) => {
          setEnd('to', value);
        }}
      />
    </div>
  );
}

/**
 * A number a teacher types, committed when they leave the field or press Enter.
 *
 * Not on every keystroke: `-` and `1.` are both states of typing `-1.5`, and committing them
 * would redraw the figure twice on the way to a number that was never asked for — and the second
 * of them is not a number at all, so the edit would be refused and the field would fight back.
 */
function NumberField({
  label,
  value,
  step = 1,
  onCommit,
}: {
  readonly label: string;
  readonly value: number;
  readonly step?: number;
  readonly onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = (): void => {
    const typed = draft;
    setDraft(null);
    if (typed === null || typed.trim() === '') return;

    const parsed = Number(typed);
    if (Number.isFinite(parsed)) onCommit(parsed);
  };

  return (
    <label style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ color: colour.muted, fontSize: 11 }}>{label}</span>
      <input
        type="number"
        step={step}
        className="es-field es-numeric"
        value={draft ?? String(value)}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
    </label>
  );
}

/**
 * A short quote of the source around the offending character.
 *
 * Beside a *typeset* formula, a caret counting characters of LaTeX points at nothing the user
 * can see: a fraction is one thing on screen and eleven characters of source. A fragment
 * of the source is something they can match against what they built.
 */
function fragmentAt(source: string, at: number): string {
  const from = Math.max(0, at - 6);
  const to = Math.min(source.length, at + 8);
  return `…${source.slice(from, to)}…`;
}

/** The parse error for a formula, or `null` when it reads. */
function errorIn(source: string): { readonly message: string; readonly at: number } | null {
  try {
    parseExpression(source);
    return null;
  } catch (error) {
    const at =
      typeof (error as { at?: unknown }).at === 'number' ? (error as { at: number }).at : 0;
    return { message: error instanceof Error ? error.message : String(error), at };
  }
}
